import {
  LayoutDashboard, Landmark, Receipt, FileText, FileSpreadsheet,
  Boxes, Hourglass, ClipboardCheck, Settings, Users, Scale, BookOpenText, TrendingUp,
} from "lucide-react";

const badge = (map) => ({ type: "badge", map });

const STATUS_COLORS = {
  Matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Filed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cleared: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Open: "bg-blue-50 text-blue-700 border-blue-200",
  "Pending Filing": "bg-amber-50 text-amber-700 border-amber-300",
  "Partially Invoiced": "bg-amber-50 text-amber-700 border-amber-300",
  "Mismatch (Value)": "bg-pink-50 text-pink-700 border-pink-200",
  "Missing in GSTR-2B": "bg-red-50 text-red-700 border-red-200",
  "GRN Not Invoiced": "bg-red-50 text-red-700 border-red-200",
  Shortage: "bg-red-50 text-red-700 border-red-200",
  Excess: "bg-amber-50 text-amber-700 border-amber-300",
  Yes: "bg-red-50 text-red-700 border-red-200",
  No: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const ageingColumns = (partyLabel) => [
  { key: "party_code", label: "Code", type: "mono" },
  { key: "party_name", label: partyLabel, type: "text" },
  { key: "not_due", label: "Not Due", type: "currency" },
  { key: "b0_30", label: "0-30 Days", type: "currency" },
  { key: "b31_60", label: "31-60 Days", type: "currency" },
  { key: "b61_90", label: "61-90 Days", type: "currency" },
  { key: "b90_plus", label: "90+ Days", type: "currency", highlight: true },
  { key: "total_due", label: "Total Due", type: "currency", bold: true },
  { key: "open_items", label: "Items", type: "number" },
];

const ledgerColumns = (partyLabel) => [
  { key: "posting_date", label: "Posting Date", type: "date" },
  { key: "doc_no", label: "Doc No", type: "mono" },
  { key: "doc_type", label: "Doc Type", type: "text" },
  { key: "party_code", label: `${partyLabel} Code`, type: "mono" },
  { key: "party_name", label: partyLabel, type: "text" },
  { key: "reference", label: "Reference", type: "mono" },
  { key: "debit", label: "Debit", type: "currency" },
  { key: "credit", label: "Credit", type: "currency" },
  { key: "status", label: "Status", ...badge(STATUS_COLORS) },
];

export const REPORTS = {
  "ar-ageing": {
    title: "AR Ageing",
    subtitle: "Customer receivables bucketed by overdue days — click a row to open its ledger",
    columns: ageingColumns("Customer"),
    drilldown: { to: "/reports/ar-ledger", param: "party_name" },
  },
  "ap-ageing": {
    title: "AP Ageing",
    subtitle: "Vendor payables bucketed by overdue days — click a row to open its ledger",
    columns: ageingColumns("Vendor"),
    drilldown: { to: "/reports/ap-ledger", param: "party_name" },
  },
  "ar-ledger": {
    title: "AR Ledger",
    subtitle: "Customer line items — invoices & incoming payments",
    dateField: "posting_date",
    columns: ledgerColumns("Customer"),
  },
  "ap-ledger": {
    title: "AP Ledger",
    subtitle: "Vendor line items — invoices & outgoing payments",
    dateField: "posting_date",
    columns: ledgerColumns("Vendor"),
  },
  gstr1: {
    title: "GSTR-1 Outward Supplies",
    subtitle: "B2B sales invoices for GST return filing",
    dateField: "invoice_date",
    columns: [
      { key: "period", label: "Period", type: "text" },
      { key: "invoice_no", label: "Invoice No", type: "mono" },
      { key: "invoice_date", label: "Date", type: "date" },
      { key: "customer", label: "Customer", type: "text" },
      { key: "gstin", label: "GSTIN", type: "mono" },
      { key: "taxable_value", label: "Taxable Value", type: "currency" },
      { key: "igst", label: "IGST", type: "currency" },
      { key: "cgst", label: "CGST", type: "currency" },
      { key: "sgst", label: "SGST", type: "currency" },
      { key: "invoice_value", label: "Invoice Value", type: "currency", bold: true },
      { key: "status", label: "Status", ...badge(STATUS_COLORS) },
    ],
  },
  gstr2: {
    title: "GSTR-2 Input Tax Credit",
    subtitle: "Purchase invoices reconciled against GSTR-2B",
    dateField: "invoice_date",
    columns: [
      { key: "period", label: "Period", type: "text" },
      { key: "invoice_no", label: "Invoice No", type: "mono" },
      { key: "invoice_date", label: "Date", type: "date" },
      { key: "vendor", label: "Vendor", type: "text" },
      { key: "gstin", label: "GSTIN", type: "mono" },
      { key: "taxable_value", label: "Taxable Value", type: "currency" },
      { key: "igst", label: "IGST", type: "currency" },
      { key: "cgst", label: "CGST", type: "currency" },
      { key: "sgst", label: "SGST", type: "currency" },
      { key: "itc_eligible", label: "ITC Eligible", type: "currency", bold: true },
      { key: "match_status", label: "2B Match", ...badge(STATUS_COLORS) },
    ],
  },
  gstr3b: {
    title: "GSTR-3B Summary",
    subtitle: "Monthly summary return — output tax vs ITC",
    columns: [
      { key: "period", label: "Period", type: "text", bold: true },
      { key: "outward_taxable", label: "Outward Taxable", type: "currency" },
      { key: "output_tax", label: "Output Tax", type: "currency" },
      { key: "itc_availed", label: "ITC Availed", type: "currency" },
      { key: "net_payable", label: "Net Payable (Cash)", type: "currency", highlight: true },
      { key: "paid_credit", label: "Paid via Credit", type: "currency" },
      { key: "status", label: "Status", ...badge(STATUS_COLORS) },
    ],
  },
  "stock-reco": {
    title: "Inventory Stock Reconciliation",
    subtitle: "SAP book stock vs physical count variance",
    columns: [
      { key: "material", label: "Material", type: "mono" },
      { key: "description", label: "Description", type: "text" },
      { key: "plant", label: "Plant", type: "text" },
      { key: "uom", label: "UoM", type: "text" },
      { key: "book_qty", label: "Book Qty", type: "number" },
      { key: "physical_qty", label: "Physical Qty", type: "number" },
      { key: "variance_qty", label: "Var Qty", type: "number", highlight: true },
      { key: "variance_value", label: "Var Value", type: "currency", highlight: true },
      { key: "book_value", label: "Book Value", type: "currency" },
      { key: "status", label: "Status", ...badge(STATUS_COLORS) },
    ],
  },
  "stock-ageing": {
    title: "Inventory Ageing",
    subtitle: "Stock value by age since last receipt",
    columns: [
      { key: "material", label: "Material", type: "mono" },
      { key: "description", label: "Description", type: "text" },
      { key: "plant", label: "Plant", type: "text" },
      { key: "total_qty", label: "Qty", type: "number" },
      { key: "b0_30", label: "0-30 Days", type: "currency" },
      { key: "b31_90", label: "31-90 Days", type: "currency" },
      { key: "b91_180", label: "91-180 Days", type: "currency" },
      { key: "b180_plus", label: "180+ Days", type: "currency", highlight: true },
      { key: "total_value", label: "Total Value", type: "currency", bold: true },
      { key: "slow_moving", label: "Slow Moving", ...badge(STATUS_COLORS) },
    ],
  },
  grir: {
    title: "GR/IR Purchase Register",
    subtitle: "GRN booked vs MIRO invoiced — open GR/IR items",
    dateField: "grn_date",
    columns: [
      { key: "po_no", label: "PO No", type: "mono" },
      { key: "vendor", label: "Vendor", type: "text" },
      { key: "material", label: "Material", type: "mono" },
      { key: "grn_doc", label: "GRN Doc", type: "mono" },
      { key: "grn_date", label: "GRN Date", type: "date" },
      { key: "grn_qty", label: "GRN Qty", type: "number" },
      { key: "grn_value", label: "GRN Value", type: "currency" },
      { key: "miro_doc", label: "MIRO Doc", type: "mono" },
      { key: "miro_qty", label: "MIRO Qty", type: "number" },
      { key: "miro_value", label: "MIRO Value", type: "currency" },
      { key: "open_value", label: "Open Value", type: "currency", highlight: true },
      { key: "age_days", label: "Age (d)", type: "number" },
      { key: "status", label: "Status", ...badge(STATUS_COLORS) },
    ],
  },
};

export const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard" }],
  },
  {
    label: "Sales",
    items: [{ to: "/reports/sales-performance", label: "Sales Performance", icon: TrendingUp, perm: "sales-performance" }],
  },
  {
    label: "Receivables",
    items: [
      { to: "/reports/ar-ageing", label: "AR Ageing", icon: Hourglass, perm: "ar-ageing" },
      { to: "/reports/ar-ledger", label: "AR Ledger", icon: BookOpenText, perm: "ar-ledger" },
    ],
  },
  {
    label: "Payables",
    items: [
      { to: "/reports/ap-ageing", label: "AP Ageing", icon: Landmark, perm: "ap-ageing" },
      { to: "/reports/ap-ledger", label: "AP Ledger", icon: Receipt, perm: "ap-ledger" },
    ],
  },
  {
    label: "GST Management",
    items: [
      { to: "/reports/gstr1", label: "GSTR-1 Sales", icon: FileText, perm: "gstr1" },
      { to: "/reports/gstr2", label: "GSTR-2 ITC", icon: FileSpreadsheet, perm: "gstr2" },
      { to: "/reports/gstr3b", label: "GSTR-3B Summary", icon: Scale, perm: "gstr3b" },
    ],
  },
  {
    label: "Inventory & Stock",
    items: [
      { to: "/reports/stock-reco", label: "Stock Reconciliation", icon: ClipboardCheck, perm: "stock-reco" },
      { to: "/reports/stock-ageing", label: "Inventory Ageing", icon: Boxes, perm: "stock-ageing" },
    ],
  },
  {
    label: "Procurement",
    items: [{ to: "/reports/grir", label: "GR/IR Register", icon: Receipt, perm: "grir" }],
  },
  {
    label: "System",
    items: [
      { to: "/settings", label: "SAP Connection", icon: Settings, perm: "settings" },
      { to: "/users", label: "User Management", icon: Users, adminOnly: true },
    ],
  },
];

export const PERMISSION_OPTIONS = [
  { key: "dashboard", label: "Executive Dashboard" },
  { key: "sales-performance", label: "Sales Performance" },
  { key: "ar-ageing", label: "AR Ageing" },
  { key: "ar-ledger", label: "AR Ledger" },
  { key: "ap-ageing", label: "AP Ageing" },
  { key: "ap-ledger", label: "AP Ledger" },
  { key: "gstr1", label: "GSTR-1 Sales" },
  { key: "gstr2", label: "GSTR-2 ITC" },
  { key: "gstr3b", label: "GSTR-3B Summary" },
  { key: "stock-reco", label: "Stock Reconciliation" },
  { key: "stock-ageing", label: "Inventory Ageing" },
  { key: "grir", label: "GR/IR Register" },
  { key: "settings", label: "SAP Connection" },
];
